import { db, functions } from "@/main";
import { defineOwnSystemConfigurationClientAccess } from "@/permissions/ClientPermissions";
import { SystemConfiguration } from "../models/Client";
import { AssistActivationOutcome } from "../models/ClientAdministrationRow";
import { getClientRef, getClientsAdministrationRef } from "./ClientRepository";
import { SystemConfigurationModule } from "@/store/modules/SystemConfigurationModule";

/**
 * Where this client READS its system configuration: its own document, or the group's where the group
 * defines it centrally.
 *
 * Read-only. The write path is a Cloud callable and always targets the caller's own client, so the
 * `ownRef` override this used to take for that purpose is gone.
 */
function getSystemConfigurationRef() {
  const clientsAdministrationRef = getClientsAdministrationRef();

  const documentRef =
    clientsAdministrationRef === "" || defineOwnSystemConfigurationClientAccess()
      ? getClientRef()
      : clientsAdministrationRef;

  return db
    .doc(documentRef)
    .collection("settings")
    .doc("systemConfiguration");
}

export function getSystemConfigurationOfGroupRef() {
  const clientsAdministrationRef = getClientsAdministrationRef();

  if (clientsAdministrationRef === "") {
    return null;
  }

  return db
    .doc(clientsAdministrationRef)
    .collection("settings")
    .doc("systemConfiguration");
}

export function observeSystemConfiguration(
  onNewSystemConfiguration: (systemConfiguration: SystemConfiguration) => void
) {
  return getSystemConfigurationRef().onSnapshot((snapshot) => {
    if (snapshot.exists) {
      const systemConfiguration = snapshot.data() as SystemConfiguration;
      onNewSystemConfiguration(systemConfiguration);
    }
  });
}

/**
 * Reads the system configuration once into `SystemConfigurationModule`, unless it is there already.
 *
 * For the route guard: several page gates (Zeiterfassung, Karte, Alle Geräte) read switches from
 * this document, and the observer above is started by `Scaffold`, which cannot mount before the
 * guard has resolved. On a hard page load the module would still hold the defaults, which fall back
 * to the older client switches and can disagree with the real configuration. Same reasoning as
 * `ensureRoleSettingsLoaded`.
 *
 * A failed read leaves the module unloaded, so the next navigation tries again.
 */
export async function ensureSystemConfigurationLoaded(): Promise<void> {
  if (SystemConfigurationModule.hasLoaded) {
    return;
  }

  try {
    const snapshot = await getSystemConfigurationRef().get();

    if (snapshot.exists) {
      SystemConfigurationModule.setSystemConfiguration(snapshot.data() as SystemConfiguration);
    } else {
      SystemConfigurationModule.markLoaded();
    }
  } catch {
    // Leave the defaults, which fall back to the client switches.
  }
}

/**
 * Saves the system configuration through Cloud, never straight to Firestore.
 *
 * This document is IRES-only in the UI - `VIEW_SYSTEM_CONFIGURATIONS` sits in `IRES_CAPABILITIES` -
 * but the security rules let any user of a client write anything that client owns, so the check
 * existed only in this browser. The callable re-checks it from the sign-in claims, where a caller
 * cannot reach.
 *
 * It matters more than most settings because the per-client product entitlement is planned to live
 * here, and a client able to write it could grant itself products.
 *
 * Since #2610141 IRES Cloud also runs what a change sets off (vehicles emptied when Fahrzeuge goes off,
 * Adressen geocodieren off with KARTE, ASSIST's activation when ASSIST goes on), the same way as for the
 * IRES Admin Client's Systemkonfiguration card, and answers how ASSIST's activation went.
 *
 * @param update - the fields to merge into the caller's own system configuration.
 * @returns how ASSIST's activation went (null when ASSIST was not switched on), and whether a side effect
 *   failed after the save. Rejects when the save itself failed.
 */
export async function updateSystemConfiguration(
  update: SystemConfiguration
): Promise<{ assistActivation: AssistActivationOutcome; effectsFailed: boolean }> {
  const callable = functions.httpsCallable("updateSystemConfiguration");

  const result = await callable({ update });
  const data = result.data as { assistActivation?: AssistActivationOutcome; effectsFailed?: boolean } | null;

  return { assistActivation: data?.assistActivation ?? null, effectsFailed: data?.effectsFailed === true };
}

